import { useEffect, useState } from 'react';

// In-memory cache for instant access
const avatarCache = new Map<string, string>();

// Helper to get base URL without cache buster
const getBaseUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete('t');
    return urlObj.toString();
  } catch {
    // If URL parsing fails, try simple string manipulation
    return url.split('?')[0];
  }
};

// Helper to add cache buster to URL
const addCacheBuster = (url: string): string => {
  // If URL already has a timestamp, use it as-is (it's already cache-busted)
  if (url.includes('?t=') || url.includes('&t=')) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}t=${Date.now()}`;
};

// Preload and cache an avatar image
const preloadAvatar = async (url: string, userId: string): Promise<string> => {
  // Check memory cache first
  if (avatarCache.has(userId)) {
    const cached = avatarCache.get(userId)!;
    // Verify the base URL matches (in case avatar was updated)
    if (getBaseUrl(cached) === getBaseUrl(url)) {
      return cached;
    }
    // Avatar URL changed, clear old cache
    avatarCache.delete(userId);
  }

  // Check localStorage
  const localCached = localStorage.getItem(`avatar_${userId}`);
  if (localCached) {
    // Verify the base URL matches (in case avatar was updated)
    if (getBaseUrl(localCached) === getBaseUrl(url)) {
      avatarCache.set(userId, localCached);
      return localCached;
    }
    // Avatar URL changed, clear old cache
    localStorage.removeItem(`avatar_${userId}`);
  }

  // Use the URL with cache buster
  const cachedUrl = addCacheBuster(url);
  
  // Preload the image
  const img = new Image();
  img.src = cachedUrl;
  
  // Store in both caches
  avatarCache.set(userId, cachedUrl);
  localStorage.setItem(`avatar_${userId}`, cachedUrl);
  
  return cachedUrl;
};

// Clear avatar cache for a user
export const clearAvatarCache = (userId: string) => {
  avatarCache.delete(userId);
  localStorage.removeItem(`avatar_${userId}`);
};

// Update avatar cache after upload
export const updateAvatarCache = (userId: string, url: string) => {
  const cachedUrl = addCacheBuster(url);
  avatarCache.set(userId, cachedUrl);
  localStorage.setItem(`avatar_${userId}`, cachedUrl);
};

/**
 * Hook to get cached avatar URL
 * Returns cached version immediately to prevent loading flash
 */
export const useAvatarCache = (userId: string, avatarUrl?: string | null) => {
  const [cachedUrl, setCachedUrl] = useState<string | null>(() => {
    // Try to get from memory cache immediately
    if (avatarCache.has(userId)) {
      return avatarCache.get(userId)!;
    }
    // Try localStorage
    const cached = localStorage.getItem(`avatar_${userId}`);
    if (cached) {
      avatarCache.set(userId, cached);
      return cached;
    }
    return null;
  });

  useEffect(() => {
    if (avatarUrl) {
      preloadAvatar(avatarUrl, userId).then(setCachedUrl);
    } else {
      // Clear cache if no avatar
      clearAvatarCache(userId);
      setCachedUrl(null);
    }
  }, [avatarUrl, userId]);

  return cachedUrl || avatarUrl;
};
